/**
 * toolExecutor.ts
 *
 * Defines OpenAI-compatible tool schemas and the executor that actually
 * calls Zustand store actions to manipulate the workflow canvas in real-time.
 *
 * Tools available to the LLM:
 *   - get_canvas_state  → read current nodes/edges
 *   - add_node          → addNode() → node appears on canvas
 *   - add_edge          → onConnect() → edge appears on canvas
 *   - delete_node       → deleteNode() → node removed from canvas
 */
import { useWorkflowStore } from '../../pages/WorkflowBuilder/store';
import { NODE_TYPES } from '../../pages/WorkflowBuilder/types';

/** All valid node type IDs (for the tool enum schema). */
const NODE_TYPE_IDS = NODE_TYPES.map((nt) => nt.type);

// ---------------------------------------------------------------------------
// OpenAI-compatible tool schemas
// ---------------------------------------------------------------------------
export const WORKFLOW_TOOLS = [
  {
    type: 'function',
    function: {
      name: 'get_canvas_state',
      description:
        'Lấy trạng thái hiện tại của canvas: danh sách node (id, type, label) và edge đang có. ' +
        'Luôn gọi tool này TRƯỚC khi gọi add_edge để biết đúng ID của các node.',
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'add_node',
      description:
        'Thêm một workflow node vào canvas. Trả về node_id của node vừa tạo. ' +
        'Lưu ý: account_selector chỉ được phép có 1. Các node khác cần có account_selector trước.',
      parameters: {
        type: 'object',
        properties: {
          node_type: {
            type: 'string',
            description: 'Loại node cần thêm.',
            enum: NODE_TYPE_IDS,
          },
          position: {
            type: 'object',
            description: 'Vị trí đặt node {x, y}. Nếu bỏ qua, hệ thống tự đặt hợp lý.',
            properties: {
              x: { type: 'number' },
              y: { type: 'number' },
            },
          },
        },
        required: ['node_type'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'add_edge',
      description:
        'Kết nối hai node với nhau bằng edge. Dùng node_id lấy từ get_canvas_state hoặc add_node.',
      parameters: {
        type: 'object',
        properties: {
          source_id: { type: 'string', description: 'ID của node nguồn (source).' },
          target_id: { type: 'string', description: 'ID của node đích (target).' },
        },
        required: ['source_id', 'target_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'delete_node',
      description: 'Xóa một node khỏi canvas theo ID. Không thể xóa account_selector.',
      parameters: {
        type: 'object',
        properties: {
          node_id: { type: 'string', description: 'ID của node cần xóa.' },
        },
        required: ['node_id'],
      },
    },
  },
] as const;

// ---------------------------------------------------------------------------
// Tool executor — called after LLM returns tool_calls
// ---------------------------------------------------------------------------

/**
 * Auto-calculate a position for the next action node so nodes don't overlap.
 * Places nodes in a flowing grid below the Data Zone area.
 */
function calcAutoPosition(store: ReturnType<typeof useWorkflowStore.getState>): {
  x: number;
  y: number;
} {
  const DATA_TYPES = new Set(['account_selector', 'brand_info', 'seeding_contents_manager']);
  const actionNodes = store.nodes.filter(
    (n) => n.id !== 'data-zone' && !DATA_TYPES.has(n.type || ''),
  );
  const count = actionNodes.length;
  const col = count % 3;
  const row = Math.floor(count / 3);
  return { x: 150 + col * 230, y: 150 + row * 180 };
}

/** Execute a single tool call and return a JSON-stringified result for the LLM. */
export function executeWorkflowTool(name: string, args: Record<string, any>): string {
  const store = useWorkflowStore.getState();

  switch (name) {
    // -----------------------------------------------------------------------
    case 'get_canvas_state': {
      const { nodes, edges } = store;
      return JSON.stringify({
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.type,
          label: (n.data as any)?.label ?? n.type,
        })),
        edges: edges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
        })),
      });
    }

    // -----------------------------------------------------------------------
    case 'add_node': {
      const nodesBefore = new Set(store.nodes.map((n) => n.id));
      const position: { x: number; y: number } = args.position ?? calcAutoPosition(store);

      const result = store.addNode(args.node_type as string, position);

      if (result === false) {
        return JSON.stringify({
          error: 'account_selector đã tồn tại — chỉ được phép có 1 node loại này trên canvas.',
        });
      }
      if (result === 'need_account_selector') {
        return JSON.stringify({
          error:
            'Cần có account_selector trước khi thêm node khác. ' +
            'Hệ thống đã tạo sẵn 1 account_selector — hãy gọi get_canvas_state để lấy ID của nó.',
        });
      }

      // Find the newly created node (it was added after nodesBefore snapshot)
      const newNode = useWorkflowStore
        .getState()
        .nodes.find((n) => !nodesBefore.has(n.id));

      return JSON.stringify({
        success: true,
        node_id: newNode?.id ?? 'unknown',
        node_type: args.node_type,
        label: (newNode?.data as any)?.label ?? args.node_type,
        message: `Đã thêm node "${args.node_type}" vào canvas.`,
      });
    }

    // -----------------------------------------------------------------------
    case 'add_edge': {
      const { nodes } = store;
      const sourceExists = nodes.some((n) => n.id === args.source_id);
      const targetExists = nodes.some((n) => n.id === args.target_id);

      if (!sourceExists) {
        return JSON.stringify({
          error: `Node source "${args.source_id}" không tồn tại. Gọi get_canvas_state để kiểm tra lại.`,
        });
      }
      if (!targetExists) {
        return JSON.stringify({
          error: `Node target "${args.target_id}" không tồn tại. Gọi get_canvas_state để kiểm tra lại.`,
        });
      }

      // Check if edge already exists
      const edgeExists = store.edges.some(
        (e) => e.source === args.source_id && e.target === args.target_id,
      );
      if (edgeExists) {
        return JSON.stringify({
          success: true,
          message: `Edge ${args.source_id} → ${args.target_id} đã tồn tại.`,
        });
      }

      store.onConnect({
        source: args.source_id,
        target: args.target_id,
        sourceHandle: null,
        targetHandle: null,
      });

      return JSON.stringify({
        success: true,
        message: `Đã kết nối ${args.source_id} → ${args.target_id}.`,
      });
    }

    // -----------------------------------------------------------------------
    case 'delete_node': {
      const node = store.nodes.find((n) => n.id === args.node_id);
      if (!node) {
        return JSON.stringify({ error: `Node "${args.node_id}" không tồn tại.` });
      }
      if (node.type === 'account_selector') {
        return JSON.stringify({ error: 'Không thể xóa account_selector.' });
      }
      store.deleteNode(args.node_id as string);
      return JSON.stringify({
        success: true,
        message: `Đã xóa node "${args.node_id}" khỏi canvas.`,
      });
    }

    // -----------------------------------------------------------------------
    default:
      return JSON.stringify({ error: `Tool không hợp lệ: ${name}` });
  }
}

// ---------------------------------------------------------------------------
// Single-shot plan execution
// ---------------------------------------------------------------------------

export interface PlanStep {
  tool: 'add_node' | 'add_edge' | 'delete_node';
  args: Record<string, string | number | object>;
  /** Alias assigned to the newly created node, e.g. "$new_0". Used by later add_edge steps. */
  ref?: string;
}

export interface AgentPlan {
  plan: PlanStep[];
  message: string;
}

/**
 * Parse LLM response text → AgentPlan.
 * Handles markdown code fences (```json ... ```) wrapping.
 * Returns null if the response is not a valid plan JSON.
 */
export function parsePlan(text: string): AgentPlan | null {
  const cleaned = text
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '')
    .trim();
  try {
    const parsed = JSON.parse(cleaned);
    if (
      parsed &&
      Array.isArray(parsed.plan) &&
      typeof parsed.message === 'string'
    ) {
      return parsed as AgentPlan;
    }
  } catch { /* not JSON — plain text response */ }
  return null;
}

/**
 * Resolve $ref aliases in args before executing a step.
 * "$new_0" → actual node ID stored in refs map.
 */
function resolveRefs(
  args: Record<string, any>,
  refs: Record<string, string>,
): Record<string, any> {
  const resolved: Record<string, any> = {};
  for (const [key, value] of Object.entries(args)) {
    if (typeof value === 'string' && value.startsWith('$')) {
      resolved[key] = refs[value] ?? value;
    } else {
      resolved[key] = value;
    }
  }
  return resolved;
}

/**
 * Execute an AgentPlan sequentially.
 * - add_node steps are delayed by `addNodeDelayMs` so Date.now() IDs are unique.
 * - $ref aliases are resolved as nodes are created.
 *
 * @param onStepStart  callback fired before each step (for live UI feedback)
 */
export async function executePlan(
  steps: PlanStep[],
  onStepStart?: (tool: string) => void,
  addNodeDelayMs = 1200,
): Promise<void> {
  /** $alias → actual node ID created during this plan */
  const refs: Record<string, string> = {};

  for (const step of steps) {
    onStepStart?.(step.tool);

    if (step.tool === 'add_node' && addNodeDelayMs > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, addNodeDelayMs));
    }

    const resolvedArgs = resolveRefs(step.args as Record<string, any>, refs);
    const resultStr = executeWorkflowTool(step.tool, resolvedArgs);

    // If this step has a ref alias, store the created node ID for later steps
    if (step.ref && step.tool === 'add_node') {
      try {
        const result = JSON.parse(resultStr) as Record<string, any>;
        if (result.node_id) {
          refs[step.ref] = result.node_id as string;
        }
      } catch { /* ignore */ }
    }
  }
}
